import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { SharedModule } from '@shared/shared.module'
import { ModalConfig } from '@core/model/modal-config.model'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { map, Observable } from 'rxjs'
import { ScopeUiDuration } from '@shared/components/ui-components/scope-ui-duration/scope-ui-duration.component'
import { NewTask } from '@core/model/new-task.model'
import { FormControl } from '@angular/forms'
import { LanguageService } from '@core/service/language.service'
import { ScopeUiAutocompleteComponent } from '@shared/components/ui-components/scope-ui-autocomplete/scope-ui-autocomplete.component'
import { searchInText } from '@app/shared/utils/search-utils.const'

enum PageState {
  NEW = 'new',
  EXISTING = 'existing',
}

@Component({
  selector: 'add-task-modal',
  templateUrl: './add-task-modal.component.html',
  styleUrls: ['./add-task-modal.component.scss'],
  imports: [SharedModule, ScopeUiInputComponent, ScopeUiDuration, ScopeUiAutocompleteComponent],
  standalone: true,
})
export class AddTaskModalComponent implements OnInit {
  @Input() taskTemplates$: Observable<any>
  @Input() rateCards$: Observable<any>
  @Input() newTaskTemplate = false
  @Input() scopeStartDate?: Date
  @Input() scopeEndDate?: Date
  @Output() onSubmit: EventEmitter<NewTask>
  @Output() onSubmitSavedTasks: EventEmitter<number[]>
  searchText: string
  taskTemplates: any[] = []
  title = 'Create Task'
  subtitle = 'Create a new or a series of new tasks, or add a saved task.'
  page: PageState = PageState.NEW
  editDescription = false
  newTask: NewTask
  nameFormControl = new FormControl()
  quantityFormControl = new FormControl()
  descriptionDirty = false
  selectedPeriod: { value: string; name: string } = { name: 'Custom', value: 'custom' }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalConfig,
    private lang: LanguageService
  ) {
    this.onSubmit = new EventEmitter<NewTask>()
    this.onSubmitSavedTasks = new EventEmitter<number[]>()
    this.newTask = new NewTask()
    this.newTask.identificationType = 'TASK'
    this.newTask.sourceType = 'NONE'

    // Use scope dates as defaults if provided, otherwise fall back to current date + 3 months
    if (this.scopeStartDate && this.scopeEndDate) {
      this.newTask.startDate = new Date(this.scopeStartDate)
      this.newTask.endDate = new Date(this.scopeEndDate)
    } else {
      this.newTask.startDate = new Date()
      this.newTask.endDate = new Date()
      this.newTask.endDate.setMonth(this.newTask.startDate.getMonth() + 3)
    }
  }

  ngOnInit() {
    // Update task dates if scope dates are provided after construction
    if (this.scopeStartDate && this.scopeEndDate) {
      this.newTask.startDate = new Date(this.scopeStartDate)
      this.newTask.endDate = new Date(this.scopeEndDate)
    }

    if (!this.newTaskTemplate) {
      this.taskTemplates$
        .pipe(
          map((taskTemplates: any[]) => {
            this.taskTemplates = taskTemplates
          })
        )
        .subscribe()
    }
  }

  onSearch(event: string) {
    this.searchText = event
  }

  checkAll() {
    this.getFilteredTemplates().forEach((template) => {
      template.selected = true
    })
  }

  clear() {
    this.taskTemplates.forEach((template) => {
      template.selected = false
    })
  }

  getFilteredTemplates() {
    return this.searchText?.length
      ? this.taskTemplates.filter((item) => searchInText(item.name, this.searchText))
      : this.taskTemplates
  }

  createNewTasks() {
    this.page = PageState.NEW
    this.title = 'Create Task'
    this.subtitle = 'Create a new or a series of new tasks, or add a saved task.'
  }

  addSavedTasks() {
    this.page = PageState.EXISTING
    this.title = 'Add Saved Task(s)'
    this.subtitle = `Find task(s) and add them to the ${this.lang.get('scope|l')}.`
  }

  getTaskName(template: any) {
    if (this.searchText) {
      const search = new RegExp(this.searchText, 'gi')
      return template.name.replace(search, '<b>$&</b>')
    }
    return template.name
  }

  tasksCount() {
    return this.taskTemplates.filter((template) => template.selected).length
  }

  confirm() {
    this.onSubmit.emit(this.newTask)
  }

  confirmSavedTasks() {
    this.onSubmitSavedTasks.emit(
      this.taskTemplates.filter((template) => template.selected).map((template) => template.id)
    )
  }

  isNewTasksFormValid() {
    return (
      this.newTask.name?.length &&
      this.newTask.description?.length &&
      (this.newTaskTemplate ? !!this.newTask.rateCardVersion : this.newTask.quantity > 0)
    )
  }

  isSavedTasksFormValid() {
    return this.tasksCount() > 0
  }

  getSelectedTemplates() {
    return this.taskTemplates.filter((template) => template.selected)
  }

  protected readonly PageState = PageState
}
